import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Calendar,
  Users,
  MessageSquare,
  Package,
  Image,
  Star,
  Check,
  Trash2,
  Send,
  X,
  Eye,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ServiceManager from '../components/admin/ServiceManager';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { t, language, dir, getLocalizedField } = useLanguage();
  const { user, token, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState({});
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Response modal
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [sendingResponse, setSendingResponse] = useState(false);
  const [expandedBookings, setExpandedBookings] = useState({});

  useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) {
      navigate('/auth?mode=login');
    }
  }, [user, isAdmin, authLoading, navigate]);

  useEffect(() => {
    if (token && isAdmin) {
      fetchData();
    }
  }, [token, isAdmin]);

  const fetchData = async () => {
    try {
      const [statsRes, bookingsRes, servicesRes, contactsRes, testimonialsRes, galleryRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/bookings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/services`),
        axios.get(`${API}/admin/contacts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/testimonials`),
        axios.get(`${API}/gallery`),
      ]);

      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setServices(servicesRes.data);
      setContacts(contactsRes.data);
      setTestimonials(testimonialsRes.data);
      setGallery(galleryRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(
        `${API}/admin/bookings/${bookingId}/status?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const sendBookingResponse = async () => {
    if (!selectedBooking || !responseMessage.trim()) {
      toast.error('Veuillez écrire un message');
      return;
    }

    setSendingResponse(true);
    try {
      await axios.post(
        `${API}/admin/bookings/${selectedBooking.id}/respond`,
        {
          booking_id: selectedBooking.id,
          response_message: responseMessage,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Réponse envoyée au client');
      setSelectedBooking(null);
      setResponseMessage('');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSendingResponse(false);
    }
  };

  const deleteService = async (serviceId) => {
    if (!window.confirm('Supprimer ce service ?')) return;
    try {
      await axios.delete(`${API}/admin/services/${serviceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Service supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleBookingExpand = (bookingId) => {
    setExpandedBookings(prev => ({
      ...prev,
      [bookingId]: !prev[bookingId]
    }));
  };

  const formatPrice = (price) => new Intl.NumberFormat('fr-FR').format(price);
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getServiceName = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    return service ? getLocalizedField(service, 'title') : serviceId;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background-light flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-light pt-24 pb-16" dir={dir}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl md:text-4xl font-light text-text-primary" data-testid="admin-title">
            Administration
          </h1>
          <p className="font-body text-text-muted">
            Gérez votre plateforme Nûr Events
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white p-6 border border-primary/10">
            <Calendar className="w-6 h-6 text-primary mb-2" />
            <p className="font-heading text-3xl font-light text-text-primary">{stats.total_bookings || 0}</p>
            <p className="font-body text-sm text-text-muted">Réservations</p>
          </div>
          <div className="bg-white p-6 border border-primary/10">
            <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center mb-2">
              <span className="text-yellow-600 font-bold text-xs">{stats.pending_bookings || 0}</span>
            </div>
            <p className="font-heading text-3xl font-light text-yellow-600">{stats.pending_bookings || 0}</p>
            <p className="font-body text-sm text-text-muted">En attente</p>
          </div>
          <div className="bg-white p-6 border border-primary/10">
            <Check className="w-6 h-6 text-green-600 mb-2" />
            <p className="font-heading text-3xl font-light text-green-600">{stats.confirmed_bookings || 0}</p>
            <p className="font-body text-sm text-text-muted">Confirmées</p>
          </div>
          <div className="bg-white p-6 border border-primary/10">
            <Users className="w-6 h-6 text-blue-600 mb-2" />
            <p className="font-heading text-3xl font-light text-blue-600">{stats.total_users || 0}</p>
            <p className="font-body text-sm text-text-muted">Utilisateurs</p>
          </div>
          <div className="bg-white p-6 border border-primary/10">
            <MessageSquare className="w-6 h-6 text-purple-600 mb-2" />
            <p className="font-heading text-3xl font-light text-purple-600">{stats.new_contacts || 0}</p>
            <p className="font-body text-sm text-text-muted">Messages</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="bookings" className="space-y-6">
          <TabsList className="bg-white border border-primary/10 p-1 flex-wrap">
            <TabsTrigger value="bookings" className="font-body data-[state=active]:bg-primary data-[state=active]:text-white">
              <Calendar className="w-4 h-4 mr-2" />
              Réservations
            </TabsTrigger>
            <TabsTrigger value="services" className="font-body data-[state=active]:bg-primary data-[state=active]:text-white">
              <Package className="w-4 h-4 mr-2" />
              Services
            </TabsTrigger>
            <TabsTrigger value="contacts" className="font-body data-[state=active]:bg-primary data-[state=active]:text-white">
              <MessageSquare className="w-4 h-4 mr-2" />
              Messages
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="font-body data-[state=active]:bg-primary data-[state=active]:text-white">
              <Star className="w-4 h-4 mr-2" />
              Témoignages
            </TabsTrigger>
            <TabsTrigger value="gallery" className="font-body data-[state=active]:bg-primary data-[state=active]:text-white">
              <Image className="w-4 h-4 mr-2" />
              Galerie
            </TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <div className="space-y-4">
              {bookings.length === 0 ? (
                <div className="bg-white p-12 border border-primary/10 text-center">
                  <Calendar className="w-12 h-12 text-text-muted mx-auto mb-4" />
                  <p className="font-body text-text-muted">Aucune réservation pour le moment</p>
                </div>
              ) : (
                bookings.map((booking) => (
                  <div 
                    key={booking.id} 
                    className="bg-white border border-primary/10 overflow-hidden"
                    data-testid={`admin-booking-${booking.id}`}
                  >
                    {/* Booking Header */}
                    <div className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="font-body text-sm font-medium text-primary">{booking.reference}</span>
                          <span className={`px-2 py-0.5 text-xs font-body ${
                            booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                            booking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            booking.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {booking.status === 'confirmed' ? 'Confirmé' :
                             booking.status === 'cancelled' ? 'Annulé' :
                             booking.status === 'completed' ? 'Terminé' : 'En attente'}
                          </span>
                          <span className={`px-2 py-0.5 text-xs font-body ${
                            booking.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                          }`}>
                            {booking.payment_status === 'paid' ? '✓ Payé' : 'Non payé'}
                          </span>
                          {booking.admin_response && (
                            <span className="px-2 py-0.5 text-xs font-body bg-purple-100 text-purple-800">
                              Répondu
                            </span>
                          )}
                        </div>
                        <h3 className="font-heading text-lg font-medium text-text-primary">
                          {booking.contact_name}
                        </h3>
                        <p className="font-body text-sm text-text-muted">
                          {booking.contact_email} | {booking.contact_phone}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <select
                          value={booking.status}
                          onChange={(e) => updateBookingStatus(booking.id, e.target.value)}
                          className="font-body text-sm border border-primary/20 px-3 py-2 bg-white"
                        >
                          <option value="pending">En attente</option>
                          <option value="confirmed">Confirmé</option>
                          <option value="cancelled">Annulé</option>
                          <option value="completed">Terminé</option>
                        </select>
                        
                        <button
                          onClick={() => {
                            setSelectedBooking(booking);
                            setResponseMessage(booking.admin_response || '');
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-primary text-white font-body text-sm hover:bg-primary/90 transition-colors"
                          data-testid={`respond-booking-${booking.id}`}
                        >
                          <Send className="w-4 h-4" />
                          Répondre
                        </button>
                        
                        <button
                          onClick={() => toggleBookingExpand(booking.id)}
                          className="p-2 border border-primary/20 hover:bg-primary/5 transition-colors"
                        >
                          {expandedBookings[booking.id] ? (
                            <ChevronUp className="w-5 h-5 text-text-muted" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-text-muted" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {expandedBookings[booking.id] && (
                      <div className="border-t border-primary/10 p-4 bg-surface-light">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <p className="font-body text-xs text-text-muted mb-1">Service</p>
                            <p className="font-body text-sm text-text-primary">
                              {getServiceName(booking.service_id)}
                              {booking.sub_service_type && ` - ${booking.sub_service_type}`}
                            </p>
                          </div>
                          <div>
                            <p className="font-body text-xs text-text-muted mb-1">Date événement</p>
                            <p className="font-body text-sm text-text-primary">{booking.event_date}</p>
                          </div>
                          <div>
                            <p className="font-body text-xs text-text-muted mb-1">Type</p>
                            <p className="font-body text-sm text-text-primary">{booking.event_type}</p>
                          </div>
                          <div>
                            <p className="font-body text-xs text-text-muted mb-1">Lieu</p>
                            <p className="font-body text-sm text-text-primary">{booking.location}</p>
                          </div>
                          <div>
                            <p className="font-body text-xs text-text-muted mb-1">Invités</p>
                            <p className="font-body text-sm text-text-primary">{booking.guest_count || '-'}</p>
                          </div>
                          <div>
                            <p className="font-body text-xs text-text-muted mb-1">Budget</p>
                            <p className="font-body text-sm text-text-primary">{booking.budget_range || '-'}</p>
                          </div>
                          {booking.rental_duration_days && (
                            <div>
                              <p className="font-body text-xs text-text-muted mb-1">Durée</p>
                              <p className="font-body text-sm text-text-primary">{booking.rental_duration_days} jours</p>
                            </div>
                          )}
                          {booking.vehicle_type && (
                            <div>
                              <p className="font-body text-xs text-text-muted mb-1">Véhicule</p>
                              <p className="font-body text-sm text-text-primary">{booking.vehicle_type}</p>
                            </div>
                          )}
                          {booking.venue_type && (
                            <div>
                              <p className="font-body text-xs text-text-muted mb-1">Type de salle</p>
                              <p className="font-body text-sm text-text-primary">{booking.venue_type}</p>
                            </div>
                          )}
                          <div>
                            <p className="font-body text-xs text-text-muted mb-1">Montant</p>
                            <p className="font-heading text-lg text-primary">{formatPrice(booking.total_amount || 0)} XAF</p>
                          </div>
                        </div>
                        
                        {booking.notes && (
                          <div className="mt-4 pt-4 border-t border-primary/10">
                            <p className="font-body text-xs text-text-muted mb-1">Notes du client</p>
                            <p className="font-body text-sm text-text-primary bg-white p-3 border border-primary/10">
                              {booking.notes}
                            </p>
                          </div>
                        )}
                        
                        {booking.admin_response && (
                          <div className="mt-4 pt-4 border-t border-primary/10">
                            <p className="font-body text-xs text-text-muted mb-1">
                              Votre réponse ({formatDate(booking.admin_responded_at)})
                            </p>
                            <p className="font-body text-sm text-text-primary bg-primary/5 p-3 border-l-4 border-primary">
                              {booking.admin_response}
                            </p>
                          </div>
                        )}
                        
                        <div className="mt-4 pt-4 border-t border-primary/10 text-xs text-text-muted">
                          Créé le: {formatDate(booking.created_at)}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services">
            <ServiceManager services={services} token={token} onRefresh={fetchData} />
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <div className="space-y-4">
              {contacts.length === 0 ? (
                <div className="bg-white p-12 border border-primary/10 text-center">
                  <MessageSquare className="w-12 h-12 text-text-muted mx-auto mb-4" />
                  <p className="font-body text-text-muted">Aucun message</p>
                </div>
              ) : (
                contacts.map((contact) => (
                  <div key={contact.id} className="bg-white p-6 border border-primary/10" data-testid={`admin-contact-${contact.id}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-heading text-lg font-medium text-text-primary">{contact.name}</p>
                        <p className="font-body text-sm text-text-muted">{contact.email} | {contact.phone}</p>
                      </div>
                      <span className="px-2 py-1 text-xs font-body bg-primary/10 text-primary">
                        {contact.reference}
                      </span>
                    </div>
                    <p className="font-body text-sm font-medium text-text-primary mb-2">{contact.subject}</p>
                    <p className="font-body text-sm text-text-muted">{contact.message}</p>
                    <p className="font-body text-xs text-text-muted mt-3">{formatDate(contact.created_at)}</p>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="bg-white p-6 border border-primary/10">
                  <div className="flex gap-1 mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-secondary text-secondary" />
                    ))}
                  </div>
                  <p className="font-body text-sm text-text-primary italic mb-4">
                    "{getLocalizedField(testimonial, 'content')}"
                  </p>
                  <p className="font-heading text-sm font-medium text-text-primary">{testimonial.client_name}</p>
                  <p className="font-body text-xs text-text-muted">{testimonial.client_title}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Gallery Tab */}
          <TabsContent value="gallery">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {gallery.map((item) => (
                <div key={item.id} className="bg-white border border-primary/10 overflow-hidden">
                  <img src={item.image_url} alt="" className="w-full h-32 object-cover" />
                  <div className="p-3">
                    <p className="font-body text-sm text-text-primary truncate">
                      {getLocalizedField(item, 'title')}
                    </p>
                    <p className="font-body text-xs text-text-muted">{item.category}</p>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Response Modal */}
      {selectedBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-lg p-6 shadow-xl" data-testid="response-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl font-medium text-text-primary">
                Répondre à {selectedBooking.contact_name}
              </h2>
              <button
                onClick={() => setSelectedBooking(null)}
                className="p-2 hover:bg-surface-light transition-colors"
              >
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            <div className="mb-4 p-3 bg-surface-light">
              <p className="font-body text-sm text-text-muted">
                Réservation: <span className="text-primary">{selectedBooking.reference}</span>
              </p>
              <p className="font-body text-sm text-text-muted">
                Email: {selectedBooking.contact_email}
              </p>
            </div>

            <div className="mb-6">
              <label className="block font-body text-sm text-text-muted mb-2">
                Votre message
              </label>
              <textarea
                value={responseMessage}
                onChange={(e) => setResponseMessage(e.target.value)}
                rows={6}
                className="w-full border border-primary/20 p-4 font-body text-sm focus:border-primary focus:outline-none resize-none"
                placeholder="Bonjour, suite à votre demande de réservation..."
                data-testid="response-textarea"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setSelectedBooking(null)}
                className="flex-1 btn-outline"
              >
                Annuler
              </button>
              <button
                onClick={sendBookingResponse}
                disabled={sendingResponse || !responseMessage.trim()}
                className="flex-1 btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="send-response-btn"
              >
                {sendingResponse ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Envoyer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
